import { DeviceConfig } from "./device.types";

/**
 * A treatment protocol template is either a device montage or a regular plan. One kind per
 * row, so a patient's assignment is naturally either/or.
 */
export type TreatmentProtocolKind = "device" | "regular";

/**
 * When the questionnaires under a protocol are sent to the patient. Mirrors the server's
 * ProtocolQuestionnaireTriggers — the numbers are the contract, so they are used directly rather
 * than translated into a parallel set of string kinds on the way in and out.
 */
export const PROTOCOL_FREQUENCY_AFTER_EACH = 1;
export const PROTOCOL_FREQUENCY_SEQUENTIAL = 2;
export const PROTOCOL_FREQUENCY_BEFORE_FIRST = 3;
export const PROTOCOL_FREQUENCY_MID_COURSE = 4;
export const PROTOCOL_FREQUENCY_AFTER_LAST = 5;

export type TProtocolFrequencyType = 1 | 2 | 3 | 4 | 5;

/** The five choices, in the order they are offered. */
export const PROTOCOL_FREQUENCY_OPTIONS: {
  value: TProtocolFrequencyType;
  labelKey: string;
}[] = [
  { value: PROTOCOL_FREQUENCY_AFTER_EACH, labelKey: "PROTOCOL_FREQ_AFTER_EACH" },
  { value: PROTOCOL_FREQUENCY_SEQUENTIAL, labelKey: "PROTOCOL_FREQ_SEQUENTIAL" },
  { value: PROTOCOL_FREQUENCY_BEFORE_FIRST, labelKey: "PROTOCOL_FREQ_BEFORE_FIRST" },
  { value: PROTOCOL_FREQUENCY_MID_COURSE, labelKey: "PROTOCOL_FREQ_MID_COURSE" },
  { value: PROTOCOL_FREQUENCY_AFTER_LAST, labelKey: "PROTOCOL_FREQ_AFTER_LAST" },
];

/** Only the sequential cadence carries a number of sessions. */
export const protocolFrequencyTakesEveryN = (
  frequencyType: number
): boolean => frequencyType === PROTOCOL_FREQUENCY_SEQUENTIAL;

export interface TreatmentProtocolQuestionnaire {
  questionnaireCompaniesHeadersId: number;
  frequencyType: number;
  everyNSessions?: number | null;
}

export interface TreatmentProtocol {
  id: number;
  companyId: number;
  /** Owning subsidiary, or null for a tenant-wide protocol every subsidiary inherits. */
  fakeCompanyId?: number | null;
  name: string;
  kind: TreatmentProtocolKind;
  statusId: number;
  comments?: string | null;

  /** kind === "device" */
  deviceConfig?: DeviceConfig | null;

  /** kind === "regular" */
  appointmentsSequenceId?: number | null;
  appointmentsSequenceOpenCountInPeriod?: number | null;
  countOfAppointments?: number | null;
  appointmentsDurationsId?: number | null;
  appointmentsDurationsOpenMinutes?: number | null;
  treatmentTypesId?: number | null;
  fundingSourcesId?: number | null;
  treatmentRoomId?: number | null;
  mediasId?: number | null;
  roleId?: number | null;
  genderId?: number | null;
  migzarId?: number | null;
  companyServicesId?: number | null;

  /** Treatments this protocol allows before a re-scan. The default offered on assignment. */
  defaultSessionsUntilRescan?: number | null;

  questionnaires: TreatmentProtocolQuestionnaire[];

  createDate?: string | null;
  updateDate?: string | null;
  assignedUsersCount: number;

  // ---- Only when the protocol was resolved for a specific patient ----
  /** When the patient's current period on this protocol began. */
  assignmentStartDate?: string | null;
  /** The quota that applies to this patient in this period. Null means no gate. */
  sessionsUntilRescan?: number | null;
  /** Treatments already carried out in this period. */
  sessionsCompleted?: number | null;
  /** Booked but not yet carried out. These consume the quota just the same. */
  sessionsScheduled?: number | null;
  /** What is left of the quota. */
  sessionsRemaining?: number | null;
  /** True once the quota is used up: no more treatments until a new protocol is assigned. */
  rescanDue?: boolean;
}

/** One period a patient spent on one protocol. The open one is the assignment in force. */
export interface UserTreatmentProtocolHistoryEntry {
  id: number;
  treatmentProtocolId: number;
  name: string;
  kind: TreatmentProtocolKind;
  startDate: string;
  endDate?: string | null;
  sessionsUntilRescan?: number | null;
  isCurrent: boolean;
}

/** Where a patient stands inside the protocol period in force. */
export interface TreatmentProtocolProgress {
  treatmentProtocolId: number;
  name: string;
  kind: TreatmentProtocolKind;
  startDate: string;
  sessionsUntilRescan?: number | null;
  sessionsCompleted: number;
  /** Booked but not yet carried out. These consume the quota just the same. */
  sessionsScheduled: number;
  sessionsRemaining?: number | null;
  rescanDue: boolean;
  /** Which treatment of the period a named appointment is. Zero when it has no report yet. */
  sessionNumber: number;
}

/** Create/update payload. Only the fields of the given kind are read by the server. */
export interface TreatmentProtocolSaveRequest {
  name: string;
  kind: TreatmentProtocolKind;
  statusId?: number;
  comments?: string | null;

  deviceConfig?: DeviceConfig | null;

  appointmentsSequenceId?: number | null;
  appointmentsSequenceOpenCountInPeriod?: number | null;
  countOfAppointments?: number | null;
  appointmentsDurationsId?: number | null;
  appointmentsDurationsOpenMinutes?: number | null;
  treatmentTypesId?: number | null;
  fundingSourcesId?: number | null;
  treatmentRoomId?: number | null;
  mediasId?: number | null;
  roleId?: number | null;
  genderId?: number | null;
  migzarId?: number | null;
  companyServicesId?: number | null;

  defaultSessionsUntilRescan?: number | null;

  questionnaires?: TreatmentProtocolQuestionnaire[];
}
